import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";
import { usePageTitle } from "@/hooks/usePageTitle";

// A plain-language orientation page. Tooltips (the ⓘ icons across the app)
// explain individual fields precisely; this page explains the big picture in
// everyday terms — where to start and how to manage the system. Sections that
// only platform staff can act on are gated on scope.

export function GettingStarted(): JSX.Element {
  usePageTitle("Getting started");
  const auth = useAuth();
  const isPlatform = auth.scope === "platform";
  const isAdmin = auth.role === "admin";

  return (
    <div className="space-y-6 max-w-3xl">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold">Getting started</h1>
        <p className="text-sm text-ink-300">
          A plain-English guide to what AquaTrack does and how to manage it. No
          technical background needed — look for the ⓘ icons anywhere in the app
          for a short explanation of any specific field.
        </p>
      </header>

      <Section title="What AquaTrack is">
        <p>
          AquaTrack keeps track of drinking-water compliance for public water
          systems in Florida. It reads the public records the state (FDEP)
          publishes about each system — its plants, wells, tanks, and filed
          documents — and turns them into a clear picture of{" "}
          <strong>what testing is required, what's been done, and what's
          coming due</strong>. You don't enter raw data; AquaTrack gathers it
          and flags what needs attention.
        </p>
      </Section>

      <Section title={isPlatform ? "First steps (for staff)" : "First steps"}>
        {isPlatform ? (
          <Steps>
            <Step n={1} title="Add the water systems you manage">
              Go to <NavTo to="/admin/onboard">Add PWS</NavTo>, type the
              system's PWS ID, confirm it's the right one, and assign it to a
              customer. AquaTrack pulls in its state records automatically.
            </Step>
            <Step n={2} title="Fill in the system's basic facts">
              Open the system, find the <em>System attributes</em> card, and
              answer a few questions FDEP doesn't publish (does it add chlorine?
              ozone? any waivers?). These decide which rules apply. Unsure?
              Leave it “Unknown” — the affected items show “needs info” instead
              of guessing.
            </Step>
            <Step n={3} title="Review the monitoring schedule">
              The <em>Monitoring schedule</em> shows every required test, when
              it's due, and whether AquaTrack found evidence it was done. Confirm
              or correct any row you have better information on.
            </Step>
          </Steps>
        ) : (
          <Steps>
            <Step n={1} title="Open your systems">
              <NavTo to="/systems">Systems</NavTo> lists everything assigned to
              you. The colored badges show how many issues need attention.
            </Step>
            <Step n={2} title="Open a system to see its detail">
              Each system has a dashboard: compliance status at the top, then
              the testing schedule, then its equipment and filed documents.
            </Step>
            <Step n={3} title="Check what's due">
              The <em>Monitoring schedule</em> tells you what tests are due, when,
              and whether they appear to be done.
            </Step>
          </Steps>
        )}
      </Section>

      <Section title="The screens, in plain English">
        <dl className="space-y-3">
          <ScreenRow to="/systems" name="Systems">
            The list of water systems you can see, each with a count of open
            issues. Click one to open its dashboard.
          </ScreenRow>
          <ScreenRow to="/compliance/nitrate-nitrite" name="Compliance">
            A cross-system view of nitrate/nitrite status by year — a quick way
            to spot which systems need attention.
          </ScreenRow>
          {isPlatform && (
            <ScreenRow to="/platform" name="Platform">
              The bird's-eye view across every customer and system, for
              AquaTrack staff.
            </ScreenRow>
          )}
          {isPlatform && (
            <ScreenRow to="/admin/onboard" name="Add PWS">
              Add a new water system by its PWS ID. It's checked against state
              records before anything is saved.
            </ScreenRow>
          )}
          {isPlatform && (
            <ScreenRow to="/admin/rules" name="Rule Library">
              The master list of testing rules (limits, how often, what triggers
              a follow-up). This is the “rule book” the schedule is built from.
            </ScreenRow>
          )}
          {isAdmin && (
            <ScreenRow to="/admin/invitations" name="Invitations">
              Invite a new user by email and choose what they can see and do.
            </ScreenRow>
          )}
          {isPlatform && isAdmin && (
            <ScreenRow to="/admin/smtp" name="SMTP">
              The email-server settings AquaTrack uses to send invitation links.
            </ScreenRow>
          )}
        </dl>
      </Section>

      <Section title="What the status colors mean">
        <p className="mb-3">
          On a system's monitoring schedule, each required test shows one of
          these:
        </p>
        <dl className="space-y-2">
          <StatusRow pill="pill-ok" label="Done">
            AquaTrack found evidence the test was filed recently enough. Nothing
            to do.
          </StatusRow>
          <StatusRow pill="pill-low" label="Due">
            The test is coming up. Make sure it gets sampled and filed on time.
          </StatusRow>
          <StatusRow pill="pill-high" label="Overdue">
            The deadline has passed without evidence of a sample. Act on this
            first.
          </StatusRow>
          <StatusRow pill="pill-medium" label="Needs info">
            AquaTrack can't tell whether this rule applies until someone fills
            in a system fact (in <em>System attributes</em>). It is never
            silently treated as done.
          </StatusRow>
        </dl>
      </Section>

      {isPlatform && (
        <Section title="Managing the system (staff)">
          <dl className="space-y-3">
            <Term name="Add or remove water systems">
              Use <NavTo to="/admin/onboard">Add PWS</NavTo>. A system always
              belongs to a customer (tenant); you can create a new customer or
              add to an existing one in the same step.
            </Term>
            <Term name="Keep the rule book current">
              The <NavTo to="/admin/rules">Rule Library</NavTo> holds every
              limit and frequency. Rules are date-stamped, so you can stage a
              future change ahead of time. Rules marked “needs review” are
              starting values to confirm. <strong>Nothing is hard-coded</strong>{" "}
              — what's in the library is what the app enforces.
            </Term>
            <Term name="Set per-system facts">
              On each system, the <em>System attributes</em> card captures the
              things state records don't publish (treatment chemicals, asbestos
              pipe, waivers). These switch the right rules on and off.
            </Term>
            <Term name="Confirm or correct the schedule">
              On any schedule row you can record a last-sampled date, mark it
              confirmed, or set a custom frequency/next-due date (for example, a
              DEP-approved plan). Your entry always wins over the automatic
              guess.
            </Term>
            <Term name="Manage people">
              {isAdmin ? (
                <>
                  Use <NavTo to="/admin/invitations">Invitations</NavTo> to add
                  users. Choose their access: who they see (one customer vs. all)
                  and what they can do (manage, edit, or view only).
                </>
              ) : (
                <>
                  Adding users is handled by an administrator from the
                  Invitations screen.
                </>
              )}
            </Term>
          </dl>
        </Section>
      )}

      <Section title="Plain-English glossary">
        <dl className="space-y-2">
          <Term name="PWS (Public Water System)">
            A water system regulated by the state — identified by a 7-digit PWS
            ID. One customer can run several.
          </Term>
          <Term name="FDEP">
            The Florida Department of Environmental Protection — the state
            agency whose public records AquaTrack reads.
          </Term>
          <Term name="MCL / Action Level">
            The legal limit for a contaminant. An <em>MCL</em> is a health limit
            you must stay under; an <em>Action Level</em> (e.g. lead, copper) is
            a threshold that triggers required steps rather than an automatic
            violation.
          </Term>
          <Term name="Monitoring / sampling">
            Collecting a water sample and sending it to a lab on a set schedule
            to prove the water is safe. AquaTrack tracks when each is due.
          </Term>
          <Term name="DBP (Disinfection Byproduct)">
            Compounds (like TTHM and HAA5) that form when a disinfectant such as
            chlorine reacts with water. Only tracked if the system disinfects.
          </Term>
          <Term name="LSLI (Lead Service Line Inventory)">
            A required list of which service-line pipes are lead, non-lead, or
            unknown — part of the federal lead rules.
          </Term>
          <Term name="Groundwater vs. surface water (Subpart H / GWUDI)">
            Wells draw groundwater; some are close enough to surface water to be
            regulated like it (“GWUDI”). Surface-influenced systems face extra
            treatment rules. AquaTrack reads this classification from FDEP.
          </Term>
          <Term name="CWS / NTNCWS / TNCWS">
            System types by who they serve: a <em>Community</em> system (homes),
            a <em>Non-Transient Non-Community</em> system (e.g. a school or
            workplace), or a <em>Transient</em> system (e.g. a rest stop).
            Different types follow different rules.
          </Term>
        </dl>
      </Section>

      <Section title="Where to get help">
        <p>
          Hover or tap the <strong>ⓘ</strong> icon next to any field for a short
          explanation — every configuration setting has one. Come back to this
          page any time from <NavTo to="/getting-started">Getting started</NavTo>{" "}
          in the top menu.
        </p>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <section className="card space-y-2">
      <h2 className="card-title">{title}</h2>
      <div className="text-sm text-ink-200 leading-relaxed space-y-2">{children}</div>
    </section>
  );
}

function Steps({ children }: { children: ReactNode }): JSX.Element {
  return <ol className="space-y-3">{children}</ol>;
}

function Step({ n, title, children }: { n: number; title: string; children: ReactNode }): JSX.Element {
  return (
    <li className="flex gap-3">
      <span className="flex-none w-6 h-6 rounded-full bg-ink-700 text-ink-100 text-xs font-semibold grid place-items-center">
        {n}
      </span>
      <div>
        <div className="font-medium text-ink-100">{title}</div>
        <div className="text-ink-300">{children}</div>
      </div>
    </li>
  );
}

function ScreenRow({ to, name, children }: { to: string; name: string; children: ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col sm:flex-row sm:gap-3">
      <dt className="sm:w-32 flex-none">
        <Link to={to} className="text-ink-100 font-medium hover:underline">
          {name}
        </Link>
      </dt>
      <dd className="text-ink-300">{children}</dd>
    </div>
  );
}

function StatusRow({ pill, label, children }: { pill: string; label: string; children: ReactNode }): JSX.Element {
  return (
    <div className="flex items-start gap-3">
      <span className={`${pill} flex-none`}>{label}</span>
      <span className="text-ink-300">{children}</span>
    </div>
  );
}

function Term({ name, children }: { name: string; children: ReactNode }): JSX.Element {
  return (
    <div>
      <dt className="text-ink-100 font-medium">{name}</dt>
      <dd className="text-ink-300">{children}</dd>
    </div>
  );
}

function NavTo({ to, children }: { to: string; children: ReactNode }): JSX.Element {
  return (
    <Link to={to} className="text-ink-100 underline hover:no-underline">
      {children}
    </Link>
  );
}
